import React, { PureComponent } from 'react';
import { connect } from '@optum-uhone-hmkts/rise';
import { enforceStylesType, getBaseIconStyle } from '../utilities/styles_util';
import { themePalette } from '../utilities/branding';
import { Paper, Grid, Button, IconButton } from '@material-ui/core';
import ArrowForward from '@material-ui/icons/ArrowForward';
import { SetupTwilioClient, IncrementHouseholdIndex, EndClickToCallSession, C2CSelectedContact, EndClickToCallPhoneCall} from '../actions/click_to_call_actions';
import { AudioSettingsDialog } from '../components/click_to_call/audio_settings_dialog';
import { ClickToCallSession, ContactPhoneNumberResult, SessionHousehold } from '../reducers/click_to_call_reducer';
import { timeFromSeconds } from '../utilities/utilities';
import { MoreMenu, createMenuAction } from '../components/nav/more_menu';
import { normalizePhone } from '../utilities/formatting/data_normalizations';
import { Strings } from '../assets/common/strings';
import { NavigationProps, navRoutes } from '../components/nav/Routes';
import { navigateTo } from '../actions/navigation_actions';
import { isMobileDevice } from '../utilities/is_mobile';
import { getTwilioDevice } from '../utilities/twilio/twilio_device';

interface StateProps {
    enabled: boolean;
    twilioToken: string;
    callsMade: number;
    callTimer: number;
    inCall: boolean;
    totalCallTimer: number;
    householdIndex: number;
    contact?: C2CSelectedContact;
    sessionHousehold?: SessionHousehold,
    session?: ClickToCallSession,
    callerIdVerified?: boolean;
    households: ContactPhoneNumberResult[];
    isTwilioSetup: boolean;
}
interface DispatchProps {
    endCall: () => void;
    endSession: () => void;
    setupTwilio: () => void;
    incrementHouseholds: () => void;
    navigateTo: (route: string) => void;
}
type Props = StateProps & DispatchProps & NavigationProps;

interface State {
    showAudioSettings: boolean;
}

class _ClickToCall extends PureComponent<Props, State> {

    state = {
        showAudioSettings: false,
    };

    componentDidMount() {    
        if (!this.props.twilioToken && !this.props.isTwilioSetup) {
            this.props.setupTwilio();
        }
    }

    componentDidUpdate(prevProps: Props) {
        // Navigate to the next household
        if (this.props.householdIndex != prevProps.householdIndex && this.props.householdIndex < this.props.households.length) {
            this.props.navigateTo(navRoutes.household.path.replace(
                Strings.Navigation.HouseholdId,
                this.props.households[this.props.householdIndex].householdId
            ));
        }
    }

    endCallOrSession = () => {
        const { inCall, contact } = this.props;

        if (inCall || contact) {
            this.props.endCall();
        } else {
            this.props.endSession();
        }
    };

    openAudioSettingsDialog = () => {
        this.setState({ showAudioSettings: true });
    }

    closeAudioSettingsDialog = () => {
        this.setState({ showAudioSettings: false });
    }

    render() {
        const { enabled, contact, inCall, callTimer, totalCallTimer, householdIndex, callsMade, households, incrementHouseholds } = this.props;

        const totalCallTime = timeFromSeconds(totalCallTimer);
        const callTime = timeFromSeconds(callTimer);
        
        const callPendingOrActive = Boolean(contact) || inCall;

        const buttonText = callPendingOrActive
            ? 'End Call'
            : 'End Session';

        return (
            <div>
                {
                    enabled && (
                        <Paper elevation={0} style={styles.paper}>
                            <Grid container spacing={0} style={styles.card} alignItems="center">
                                <Grid container xs={4} sm={4} style={styles.c2cText} direction="column">
                                    <Grid item style={styles.cardData}>{`Total Call Time: ${totalCallTime}`}</Grid>
                                    <Grid item style={styles.cardData}>{`Total Calls Made: ${callsMade}`}</Grid>
                                </Grid>
                                <Grid container xs={4} sm={4} style={{ ...styles.c2cText, textAlign: 'center' }} direction="column">
                                    <Grid item style={styles.cardData}>{`Calling: ${contact ? contact.name : 'No phone call started'}`}</Grid>
                                    <Grid item style={styles.cardData}>{`${contact ? normalizePhone(contact.number) : ''}`}</Grid>
                                </Grid>
                                <Grid container xs={4} sm={4} style={styles.c2cText} direction="row" justify="flex-end">
                                    <Grid direction="column" style={{ textAlign: 'right' }}>
                                        <Grid item style={styles.cardData}>{`Call Duration: ${callTime}`}</Grid>
                                        <Grid item style={styles.cardData}>{`${householdIndex + 1} of ${households.length} Households`}</Grid>
                                    </Grid>
                                    <MoreMenu>
                                        {[createMenuAction('Audio Settings', this.openAudioSettingsDialog, this.props.session == undefined)]}
                                    </MoreMenu>
                                </Grid>
                            </Grid>
                            <Grid container style={{ ...styles.card, maxHeight: 50 }} justify="flex-end" alignItems="center" direction="row">
                                <Grid container xs={6} sm={6} justify="flex-start" alignContent="center" alignItems="center">
                                    <Button
                                        onClick={this.endCallOrSession}
                                        style={{ backgroundColor: themePalette.primary, color: themePalette.negative_text, maxHeight: 20, marginLeft: 20 }}
                                    >
                                        {buttonText}
                                    </Button>
                                </Grid>
                                <Grid container xs={6} sm={6} style={{ marginBottom: 10 }} justify="flex-end" alignContent="center" alignItems="center">
                                    <IconButton
                                        onClick={incrementHouseholds}
                                        disabled={callPendingOrActive || householdIndex >= households.length - 1}
                                        style={getBaseIconStyle(themePalette.negative_icon)}
                                    >
                                        <ArrowForward />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Paper>
                    )
                }
                {this.props.children}
                <AudioSettingsDialog
                    twilioDevice={getTwilioDevice()}
                    closeDialog={this.closeAudioSettingsDialog}
                    visible={this.state.showAudioSettings}
                />
            </div>
        );
    }

}

const styles = enforceStylesType({
    card: {
        overflow: 'hidden',
        flexWrap: 'nowrap',
    },
    paper: {
        borderRadius: 0,
        backgroundColor: themePalette.secondary,
        color: themePalette.negative_text,
    },
    cardData: {
        fontSize: 14,
        cursor: 'default',
    },
    c2cText: {
        marginLeft: 10,
        marginTop: 10,
        marginRight: 10,
    }
});

const mapStateToProps = (state): StateProps => {
    

    const { sessionHousehold } = state.clickToCall;
    return {
        enabled: state.clickToCall.enabled && !isMobileDevice,
        twilioToken: state.clickToCall.twilioToken,
        callsMade: state.clickToCall.metadata.callsMade,
        callTimer: state.clickToCall.metadata.callTimer,
        inCall: state.clickToCall.metadata.inCall,
        totalCallTimer: state.clickToCall.metadata.totalCallTimer,
        householdIndex: state.clickToCall.metadata.householdIndex,
        contact: state.clickToCall.metadata.contact,
        sessionHousehold,
        session: state.clickToCall.session,
        callerIdVerified: state.clickToCall.callerIdVerified,
        households: sessionHousehold ? sessionHousehold.households.filter(house => house.dncReason == null) : [],
        isTwilioSetup: state.clickToCall.isTwilioSetup,
    }
};
const mapDispatchToProps = (dispatch): DispatchProps => ({
    endCall: () => dispatch(EndClickToCallPhoneCall()),
    endSession: () => dispatch(EndClickToCallSession.started(undefined)),
    setupTwilio: () => dispatch(SetupTwilioClient.started(undefined)),
    incrementHouseholds: () => dispatch(IncrementHouseholdIndex()),
    navigateTo: (route: string) => dispatch(navigateTo(route)),
});

export const ClickToCall = connect(mapStateToProps, mapDispatchToProps, true)(_ClickToCall);
